import { useState, useEffect, useMemo } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import checkGreen from '../../assets/images/icons/checkGreen.svg';
import B2BApiService from '../../B2bApiService';
import { getAONAssessmentLabelById } from '../../Utilities';
import { t } from '../../content';
import { useLocale } from '../../locale';

export function buildAssessmentNameMap(assessmentList, locale) {
    const map = {};
    assessmentList.forEach(a => {
        map[a.id] = getAONAssessmentLabelById(parseInt(a.instrument, 10), locale) || a.name;
    });
    return map;
}

// assessmentNames: optional {[assessmentId]: string} — when omitted the component fetches internally on first expand.
function AssessmentProgress({ requiredAssessments = [], completedAssessments = [], assessmentNames = null }) {
    const { locale } = useLocale();
    const totalRequired = requiredAssessments.length;
    const [isExpanded, setIsExpanded] = useState(false);
    const [fetchedAssessments, setFetchedAssessments] = useState([]);

    const b2bServiceObj = useMemo(() => new B2BApiService(), []);

    useEffect(() => {
        if (isExpanded && totalRequired > 0 && !assessmentNames && !fetchedAssessments.length) {
            b2bServiceObj.LoadAssessments(setFetchedAssessments);
        }
    }, [isExpanded, totalRequired, assessmentNames, fetchedAssessments.length, b2bServiceObj]);

    const nameMap = useMemo(() => {
        if (assessmentNames) return assessmentNames;
        return buildAssessmentNameMap(fetchedAssessments, locale);
    }, [assessmentNames, fetchedAssessments, locale]);

    if (totalRequired === 0) return null;

    const totalCompleted = completedAssessments.filter(id => requiredAssessments.includes(id)).length;
    const percentage = Math.round((totalCompleted / totalRequired) * 100);

    return (
        <>
            <div className="assessment-progress">
                <div className="assessment-progress-summary">
                    {t('teams.progress.completedOfRequired', { completed: totalCompleted, total: totalRequired })}
                    <InfoOutlinedIcon
                        className="required-note-icon"
                        aria-label={t('teams.progress.requiredNote')}
                    />
                </div>
                <div className="assessment-progress-slider">
                    <div className="assessment-progress-bar">
                        <div
                            className="assessment-progress-bar-fill"
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                    <div className="assessment-progress-bar-indicator">{percentage}%</div>
                    <button
                        className="assessment-progress-toggle mar-20 left"
                        onClick={() => setIsExpanded(prev => !prev)}
                        aria-expanded={isExpanded}
                    >
                        {t('teams.progress.moreDetails')} {isExpanded
                            ? <KeyboardDoubleArrowUpIcon />
                            : <KeyboardDoubleArrowDownIcon />
                        }
                    </button>
                </div>
            </div>

            <div className={`assessment-progress-expandable${isExpanded ? ' open' : ''}`}>
                <ul className="assessment-progress-details">
                    {requiredAssessments.map(id => {
                        const done = completedAssessments.includes(id);
                        return (
                            <li key={id} className={`assessment-progress-detail-item${done ? ' completed' : ' pending'}`}>
                                {done
                                    ? <img src={checkGreen} alt={t('teams.progress.completedAlt')} className="detail-status-icon" />
                                    : <RadioButtonUncheckedIcon className="detail-status-icon" />
                                }
                                <span>{nameMap[id] || t('teams.progress.assessmentNumber', { id })}</span>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </>
    );
}

export default AssessmentProgress;
